import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import ReactDOM from 'react-dom';

import TopHeader from '../template/TopHeader';
import CalendarCard from '../Diary/CalendarCard';
import { DiaryProvider, ACTIONS } from './DiaryContext';
import { DragProvider } from './DragContext';
import DiaryViewport from './DiaryViewport';
import DragOverlay from './DragOverlay';
import DiaryShelf from './DiaryShelf';
import DiaryFAB from './DiaryFAB';
import FirstTasks from '../onboarding/FirstTasks';
import DiaryTour from '../onboarding/DiaryTour';
import { ONBOARDING_ENABLED } from '../onboarding/flags';
import { setupOff as receptionSetupOff } from '../../_utils/receptionMode';
import StaffFilter from './StaffFilter';
import BookingDetailPanel from './BookingDetailPanel';
import CheckoutPanel from '../checkout/CheckoutPanel';
import NewEventPanel from './NewEventPanel';
import ContextMenu from './ContextMenu';
import SlidePanel from '../shared/SlidePanel';
import ShiftTimePicker from '../Diary/ShiftTimePicker';
import { Link } from 'react-router-dom';
import { normalizeScheduleResponse, normalizeStaffMember, calculateTimeBounds, normalizeDiaryResponse, normalizeDiaryWeekResponse, buildResourceLanes, enrichEventResourceColours } from './normalizeData';
import { bookingActions, eventActions, hairdresserActions, productAction } from '../../_actions';
import { diarySharingActions } from '../../_actions/diarySharing.actions';
import { bookingService } from '../../_services/booking.service';
import { eventService } from '../../_services/event.service';
import toast from '../shared/Toast';
import './diary-v2.css';
import { refreshMonthView } from './MonthView';
import { splitDateRange } from './monthData';
import { isDiaryDemoOn, buildDemoDiary, demoResources } from '../demo/diaryDemo';
import { withDemoTeam, isDemoStaffId, isDemoTeamOn, setDemoTeam } from '../onboarding/demoTeam';
import { applyIntroStage, isIntroActive, isDemoBookingId, INTRO_EVENT, INTRO_CHECKOUT_EVENT, tellIntro } from '../onboarding/bailey-intro/introStage';
import BaileyIntro from '../onboarding/bailey-intro/BaileyIntro';
import { highlightBooking, highlightItem } from './diaryHighlight';
import { BAILEY_BOOK_EVENT, BAILEY_ITEM_EVENT, ITEM_CREATED_EVENT } from '../agent/bailey/baileyBooking';
import { getPlacement, isCheckingDates } from './repeat/placement';
import { freeTimesFor } from '../marketing-v2/features/social/data/fromDiary';
import { publishFacts, clearFacts } from '../agent/bailey/skills/pageFacts';
import { subscribe as subscribeSync, versionOf as syncVersionOf } from '../../_utils/syncSummary';

// The day view reloads on tab return only when the day changed, or after this.
const DIARY_MAX_STALE_MS = 10 * 60 * 1000;

var _ = require('lodash');

/**
 * DiaryV2 - Main orchestrator component.
 *
 * Class component to integrate with existing Redux + react-router patterns.
 * Wraps functional DiaryProvider/DiaryViewport for the new architecture.
 */
class DiaryV2 extends Component {
  constructor(props) {
    super(props);

    const savedDate = localStorage.getItem('cardBookingDate');
    const initialDate = this.props.location.state?.bookingDate
      ? moment(this.props.location.state.bookingDate).format('YYYY-MM-DD')
      : savedDate
        ? moment(savedDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');

    this.state = {
      bookingDate: initialDate,
      isLoading: true,
      isFirstTimeLoad: 1,
      filterOpen: false,
      resourceUnits: [],   // rooms/equipment — lanes + badges
      resourceGroups: [],
      selectedEvent: null,
      // Edit Shift modal
      openShiftDay: false,
      shiftStaff: null,
      todayStartWorking: '',
      todayEndWorking: '',
      originalStartWorking: '',
      originalEndWorking: '',
      isTodayWorking: false,
      slotDuration: parseInt(localStorage.getItem('diarySlotDuration'), 10) || 15,
      viewMode: 'day',
      // New booking/event/leave panel
      // newBookingPanel is now managed by the minimisedBooking redux slice
      // and rendered by NewBookingHost at the router level.
      newEventPanel: null,   // { mode: 'event'|'leave', staffId, date, startMinutes }
      // Context menu state
      contextMenu: null, // { event, x, y }
      contextMenuAction: null, // action to pass to panel on open
      checkoutPanel: null, // { bookingId } - new checkout panel
    };

    this.diaryRef = React.createRef();
    this.shelfRef = React.createRef();
    this._currentZoom = parseFloat(localStorage.getItem('diaryZoomLevel')) || 1.0;
  }

  // Resolve a deep-link target (URL like /diary/:salonId/booking/:id),
  // jump the diary to its date, then open the BookingDetailPanel. Silent
  // on failure so a stale or missing ID just leaves the diary on today.
  handleDeepLink = ({ type, id }) => {
    const open = (date) => {
      const dateStr = moment(date).format('YYYY-MM-DD');
      this.setState({ bookingDate: dateStr });
      this.loadDiaryData(dateStr);
      this.setState({
        selectedEvent: {
          type,
          ...(type === 'booking' ? { bookingId: id } : { eventId: id }),
          date: dateStr,
          startMinutes: 0,
          endMinutes: 0,
          title: '',
          clientName: '',
        },
        contextMenuAction: null,
      });
    };

    if (type === 'booking') {
      bookingService.getBookingByIdV2(id)
        .then((response) => {
          const detail = response?.data?.responseData;
          if (detail?.bookingDate) open(detail.bookingDate);
        })
        .catch(() => { /* silent — fall back to default diary view */ });
    } else {
      const hairdresserId = localStorage.getItem('userId');
      if (!hairdresserId) return;
      eventService.getEvent({ hairdresserId, eventId: id })
        .then((response) => {
          const detail = response?.data?.responseData;
          const date = detail?.startDate || detail?.bookingDate;
          if (date) open(date);
        })
        .catch(() => { /* silent — fall back to default diary view */ });
    }
  }

  // Dispatches openMinimisedBooking with the staff list + currency context
  // the host needs to render even on non-diary routes.
  openNewBooking = (seed) => {
    const staffList = this._staffList || [];
    const currencySymbol = this.props.user?.userInfo?.hairdresser?.currencySymbol || '£';
    this.props.dispatch(bookingActions.openMinimisedBooking({
      ...seed,
      bookingDate: seed?.bookingDate || seed?.date || this.state.bookingDate,
      staffList,
      currencySymbol,
    }));
  }

  // A booking Bailey got ready. Same panel, same seed shape as a slot tap or
  // a rebook — the only difference is who filled it in.
  openBaileyBooking = (b) => {
    const seed = {
      staffId: b.staffId || '',
      bookingDate: b.bookingDate || this.state.bookingDate,
      startMinutes: b.startMinutes,
      rebookClient: b.client || null,
      newClient: b.newClient || null,
      rebookServices: b.services || [],
      repeat: b.repeat || null,
    };
    if (seed.bookingDate && seed.bookingDate !== this.state.bookingDate) {
      this.handleDateChange(seed.bookingDate);
      // Let the day render first, so the panel opens over the right diary.
      setTimeout(() => this.openNewBooking(seed), 350);
    } else {
      this.openNewBooking(seed);
    }
  }

  // An event or time off Bailey got ready. The same panel a slot tap opens,
  // with his title, end and (for leave) last day and kind filled in.
  openBaileyItem = (b) => {
    const panel = {
      mode: b.mode === 'leave' ? 'leave' : 'event',
      staffId: b.staffId || '',
      date: b.date || this.state.bookingDate,
      startMinutes: b.startMinutes,
      prefill: { endMinutes: b.endMinutes, endDate: b.endDate, title: b.title, leaveType: b.leaveType, isBookingAllowed: b.isBookingAllowed },
      // A fresh key per proposal: the panel reads its props once, on mount.
      key: Date.now(),
    };
    const open = () => this.setState({ newEventPanel: panel, selectedEvent: null });
    if (panel.date && panel.date !== this.state.bookingDate) {
      this.handleDateChange(panel.date);
      setTimeout(open, 350);
    } else {
      open();
    }
  }

  // A booking the new leave or block lands on: open it over the panel
  // (Move opens it to change, Message goes to the client's Messages). The
  // leave panel waits underneath with what was typed.
  openBookingOverTimeOff = (b, action) => {
    if (!b || !b.bookingId) return;
    // Another one already open: close it first, so this one opens fresh
    // with its action
    if (this.state.selectedEvent) {
      this.setState({ selectedEvent: null, contextMenuAction: null }, () => setTimeout(() => this.openBookingOverTimeOff(b, action), 0));
      return;
    }
    this.setState({
      selectedEvent: {
        type: 'booking',
        bookingId: String(b.bookingId),
        date: b.date || this.state.bookingDate,
        startMinutes: b.start || 0,
        endMinutes: b.end || 0,
        title: '',
        clientName: (b.client && b.client.name) || '',
      },
      contextMenuAction: action === 'message' ? 'message' : 'edit',
    });
  }

  loadResources = () => {
    // Rooms/equipment for lanes + badges. Any staff may READ the list; a
    // salon without resources gets empty arrays and the diary is untouched.
    const axios = require('axios');
    const API = require('../../config/API').default;
    axios.get(API.RESOURCE_LIST, { headers: { accessToken: localStorage.getItem('accessToken') } })
      .then(res => {
        const data = res?.data?.responseData || {};
        this.setState({ resourceUnits: data.resources || [], resourceGroups: data.groups || [] },
          () => { if (this._lastDiaryArgs) this.processDiaryResponse(...this._lastDiaryArgs); });
      })
      .catch(() => {});
  };

  componentDidMount() {
    this.loadResources();
    // V1 adds diary-temp to body for mobile TopHeader overrides (position: static,
    // z-index: 999, etc.) — needed so the mobile toolbar shows next to hamburger
    document.body.classList.add('diary-temp');
    // Fetch diary sharing visibility before loading diary data so the
    // staff filter applies on the first render.
    this.props.dispatch(diarySharingActions.fetchMyVisibility());
    this.loadDiaryData(this.state.bookingDate);

    // URL deep-link: /diary/:salonId/(booking|event|leave)/:id opens the
    // detail panel for that entity. Runs in parallel with the regular
    // initial load so the diary chrome appears immediately; once the
    // entity's date is known, we jump the diary to it and slide the
    // panel in.
    const params = this.props.match?.params || {};
    if (params.bookingId) {
      this.handleDeepLink({ type: 'booking', id: params.bookingId });
    } else if (params.eventId) {
      this.handleDeepLink({ type: 'event', id: params.eventId });
    } else if (params.leaveId) {
      this.handleDeepLink({ type: 'holiday', id: params.leaveId });
    }

    // NewBookingHost dispatches this on save success. Refresh the diary
    // in-place if we're still mounted (the host lives at the router level
    // and may save from another route, in which case this listener won't
    // be active — that's fine, diary remount loads fresh).
    this._onBookingCreated = (e) => {
      // The new block arrives with a flourish rather than just being there.
      const newId = e && e.detail && e.detail.bookingId;
      if (newId) highlightBooking(newId);
      this.loadDiaryData(this.state.bookingDate);
      if (this.state.viewMode === 'week' && this._weekViewStaff) {
        this.loadWeekData(this._weekViewStaff);
      }
      if (this.state.viewMode === 'month') refreshMonthView();
    };
    window.addEventListener('diary:booking-created', this._onBookingCreated);
    // Bailey got a booking ready: jump to its day and open OUR new-booking
    // panel filled in. The owner saves it there — nothing is booked by Bailey.
    this._onBaileyBook = (e) => this.openBaileyBooking((e && e.detail) || {});
    window.addEventListener(BAILEY_BOOK_EVENT, this._onBaileyBook);
    // Same for an event or time off: OUR event/leave panel, filled in.
    this._onBaileyItem = (e) => this.openBaileyItem((e && e.detail) || {});
    window.addEventListener(BAILEY_ITEM_EVENT, this._onBaileyItem);
    // Onboarding: jump to the day the practice booking landed on.
    this._onGoToDate = (e) => { const d = e && e.detail && e.detail.date; if (d && d !== this.state.bookingDate) this.handleDateChange(d); };
    window.addEventListener('diary:go-to-date', this._onGoToDate);

    // Staging demo switched on or off, or re-themed: redraw the day.
    this._onDemoChanged = () => this.loadDiaryData(this.state.bookingDate);
    window.addEventListener('beu:diary-demo-changed', this._onDemoChanged);
    window.addEventListener('beu:demo-team-changed', this._onDemoChanged);
    // Bailey's welcome reshapes the day it already has, so each beat lands
    // straight away instead of flashing a loader between them.
    this._onIntroChanged = () => {
      // Re-render too: slot taps skip the appointment/event menu while the
      // intro runs, so a tap goes straight to a practice booking.
      this.setState({ introActive: isIntroActive() });
      const raw = this._rawDiary;
      const demoTakesOver = isDiaryDemoOn() && !isIntroActive();
      if (!demoTakesOver && raw && raw.dateStr === moment(this.state.bookingDate).format('YYYY-MM-DD')) {
        // One render for the whole beat, so columns and bookings arrive together.
        ReactDOM.unstable_batchedUpdates(() => this.processDiaryResponse(this.shapeDiary(raw.data, raw.dateStr), raw.dateStr));
      } else {
        this.loadDiaryData(this.state.bookingDate);
      }
    };
    window.addEventListener(INTRO_EVENT, this._onIntroChanged);
    // Bailey opens (and closes) checkout on a practice booking during the tour.
    this._onIntroCheckout = (e) => {
      const d = (e && e.detail) || {};
      if (d.action === 'open' && d.bookingId) this.setState({ checkoutPanel: { bookingId: d.bookingId }, selectedEvent: null });
      if (d.action === 'close') this.setState({ checkoutPanel: null });
    };
    window.addEventListener(INTRO_CHECKOUT_EVENT, this._onIntroCheckout);

    // An event or time off Bailey got ready from another page.
    try {
      const itemStr = sessionStorage.getItem('beu-bailey-item');
      if (itemStr) {
        sessionStorage.removeItem('beu-bailey-item');
        const item = JSON.parse(itemStr);
        setTimeout(() => this.openBaileyItem(item), 500);
      }
    } catch (e) { /* ignore */ }

    // Handle rebook from invoice/checkout (stored in sessionStorage)
    try {
      const rebookStr = sessionStorage.getItem('beu-rebook');
      if (rebookStr) {
        sessionStorage.removeItem('beu-rebook');
        const rebookData = JSON.parse(rebookStr);
        setTimeout(() => {
          // Bailey's proposals arrive this way too when he was asked from
          // another page: they carry a date and a start time.
          if (rebookData.bookingDate || rebookData.startMinutes != null) {
            this.openBaileyBooking({
              staffId: rebookData.staffId || '',
              bookingDate: rebookData.bookingDate,
              startMinutes: rebookData.startMinutes,
              client: rebookData.client || null,
              newClient: rebookData.newClient || null,
              services: rebookData.services || [],
              repeat: rebookData.repeat || null,
            });
            return;
          }
          this.openNewBooking({
            staffId: rebookData.staffId || '',
            rebookServices: rebookData.services || [],
            rebookClient: rebookData.client || null,
            newClient: rebookData.newClient || null,
          });
        }, 500);
      }
    } catch (e) { /* ignore */ }

    // Deep-link: notification panel can push /diary/<salonId> with
    // location.state.openBookingId to jump straight into a booking.
    try {
      const deepLinkBookingId = this.props.location?.state?.openBookingId;
      if (deepLinkBookingId) {
        const linkDate = this.props.location?.state?.bookingDate || this.state.bookingDate;
        setTimeout(() => {
          this.setState({
            selectedEvent: {
              type: 'booking',
              bookingId: deepLinkBookingId,
              date: linkDate,
              startMinutes: 0,
              endMinutes: 0,
              title: '',
              clientName: '',
            },
            contextMenuAction: null,
          });
          // Clear the state so refreshing doesn't re-trigger
          if (this.props.history?.replace) {
            this.props.history.replace({
              pathname: this.props.location.pathname,
              state: {},
            });
          }
        }, 450);
      }
    } catch (e) { /* ignore */ }

    // Listen for universal search booking open events
    this._searchOpenBooking = (e) => {
      if (e.detail?.bookingId) {
        // Create a minimal event object to open the booking detail panel
        this.setState({
          selectedEvent: {
            type: 'booking',
            bookingId: e.detail.bookingId,
            date: e.detail.date || this.state.bookingDate,
            startMinutes: 0,
            endMinutes: 0,
            title: '',
            clientName: '',
          },
          contextMenuAction: null,
        });
      }
    };
    document.addEventListener('beu-open-booking', this._searchOpenBooking);
    // A repeat's Check the dates: "See it in the diary" goes to that day and
    // brings the stylist's column into view (the panel waits as its pill)
    this._goToDate = (e) => { if (e.detail && e.detail.date) this.handleDayTap(e.detail.date, e.detail.staffId); };
    window.addEventListener('beu:diary-go-to-date', this._goToDate);

    // Refresh data when the browser tab regains focus. Week and month views
    // reload as they always have. The day view (where the front desk lives)
    // asks the app's routine poll for the day's diary version first and only
    // reloads when the day changed, or it was last loaded over 10 minutes
    // ago (onDiarySync below).
    this._handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (this.state.viewMode === 'day' && this._diarySync) return;
        this.loadDiaryData(this.state.bookingDate);
        if (this.state.viewMode === 'week' && this._weekViewStaff) {
          this.loadWeekData(this._weekViewStaff);
        }
        if (this.state.viewMode === 'month') refreshMonthView();
      }
    };
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    this._diarySeen = {};
    this._diarySync = subscribeSync({
      visibleAreas: ['diary'],
      params: () => {
        const d = moment(this.state.bookingDate).format('YYYY-MM-DD');
        return { diaryStart: d, diaryEnd: d };
      },
      onUpdate: this.onDiarySync,
    });

    // Direct URL entry — /diary/:salonId/booking/:bookingId etc. — opens
    // the panel on first paint. componentDidUpdate handles browser nav
    // and clicks once we're already mounted.
    this.maybeOpenFromUrl();
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-fetch only when the SALON changes — not when we just appended
    // /booking/<id> etc. onto the same diary URL via deep-link sync.
    const prevSalon = prevProps.match?.params?.salonId;
    const curSalon = this.props.match?.params?.salonId;
    if (curSalon && prevSalon !== curSalon) {
      this.loadDiaryData(this.state.bookingDate);
    }

    // Diary sharing can arrive after the day has drawn (it isn't awaited on
    // mount): redraw the columns from the same response when the list of
    // diaries this person may see changes. Compared by content, since the
    // prop is a fresh [] whenever the list is empty.
    const seen = (v) => (v || []).join(',');
    if (seen(prevProps.diaryVisibility) !== seen(this.props.diaryVisibility) && this._lastDiaryArgs) {
      this.processDiaryResponse(...this._lastDiaryArgs);
    }

    // State → URL: a click / drag / context-menu action mutates
    // selectedEvent; mirror that into the address bar so the URL is
    // shareable. syncUrlFromSelectedEvent no-ops when the path already
    // matches (the bottom guard); maybeOpenFromUrl below no-ops when the
    // target already matches selectedEvent — that pair breaks the loop.
    if (prevState.selectedEvent !== this.state.selectedEvent) {
      this.syncUrlFromSelectedEvent();
    }

    // URL → state: handles browser back/forward, direct URL entry, and the
    // history.replace we just did in the line above (no-op then, see guard).
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      this.maybeOpenFromUrl();
    }
  }

  // ── Deep-link URL ↔ selectedEvent sync ─────────────────────────────────
  getDeepLinkTarget = () => {
    const p = this.props.match?.params || {};
    if (p.bookingId) return { type: 'booking', id: p.bookingId };
    if (p.eventId) return { type: 'event', id: p.eventId };
    if (p.leaveId) return { type: 'holiday', id: p.leaveId };
    return null;
  }

  selectedEventMatchesTarget = (event, target) => {
    if (!event || !target) return false;
    if (target.type === 'booking') return event.type === 'booking' && event.bookingId === target.id;
    return event.type === target.type && event.eventId === target.id;
  }

  maybeOpenFromUrl = () => {
    const target = this.getDeepLinkTarget();
    if (!target) return;
    if (this.selectedEventMatchesTarget(this.state.selectedEvent, target)) return;
    // Minimal stub — BookingDetailPanel fetches the full record by ID.
    this.setState({
      selectedEvent: {
        type: target.type,
        bookingId: target.type === 'booking' ? target.id : undefined,
        eventId: target.type !== 'booking' ? target.id : undefined,
        date: this.state.bookingDate,
        startMinutes: 0,
        endMinutes: 0,
        title: '',
        clientName: '',
      },
      contextMenuAction: null,
    });
  }

  syncUrlFromSelectedEvent = () => {
    const salonId = this.props.match?.params?.salonId;
    if (!salonId || !this.props.history?.replace) return;
    const event = this.state.selectedEvent;
    const base = `/diary/${salonId}`;
    let path = base;
    if (event) {
      if (event.type === 'booking' && event.bookingId) {
        path = `${base}/booking/${event.bookingId}`;
      } else if (event.type === 'event' && event.eventId) {
        path = `${base}/event/${event.eventId}`;
      } else if (event.type === 'holiday' && event.eventId) {
        path = `${base}/leave/${event.eventId}`;
      } else {
        // Event without an ID (e.g. an unsaved ghost) — keep URL on base.
        path = base;
      }
    }
    if (this.props.location?.pathname !== path) {
      this.props.history.replace(path);
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    clearFacts('diary');
    document.body.classList.remove('diary-temp');
    if (this._handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    }
    if (this._diarySync) { this._diarySync(); this._diarySync = null; }
    if (this._searchOpenBooking) document.removeEventListener('beu-open-booking', this._searchOpenBooking);
    if (this._goToDate) window.removeEventListener('beu:diary-go-to-date', this._goToDate);
    if (this._onBookingCreated) window.removeEventListener('diary:booking-created', this._onBookingCreated);
    if (this._onBaileyBook) window.removeEventListener(BAILEY_BOOK_EVENT, this._onBaileyBook);
    if (this._onBaileyItem) window.removeEventListener(BAILEY_ITEM_EVENT, this._onBaileyItem);
    if (this._onGoToDate) window.removeEventListener('diary:go-to-date', this._onGoToDate);
    if (this._onDemoChanged) {
      window.removeEventListener('beu:diary-demo-changed', this._onDemoChanged);
      window.removeEventListener('beu:demo-team-changed', this._onDemoChanged);
      window.removeEventListener(INTRO_EVENT, this._onIntroChanged);
      window.removeEventListener(INTRO_CHECKOUT_EVENT, this._onIntroCheckout);
    }
  }

  // Bailey's welcome replaces the day while it runs; otherwise the two
  // onboarding colleague columns (when on) are added to the real day.
  shapeDiary = (data, dateStr) => (isIntroActive() ? applyIntroStage(data, dateStr) : withDemoTeam(data, dateStr));

  /**
   * The tab came back (day view): reload the day only if its diary version
   * moved since we last reloaded for it, or that was over 10 minutes ago.
   * The version is taken BEFORE the reload, so a change landing during it
   * makes the next check reload again rather than hiding it. Anything that
   * isn't a clean answer (an error, an older server) reloads, as before.
   */
  onDiarySync = (data, event) => {
    if (event.reason !== 'visible' || this._unmounted || this.state.viewMode !== 'day') return;
    const dateStr = moment(this.state.bookingDate).format('YYYY-MM-DD');
    const v = data.diary;
    const version = !event.error && v && v.startDate === dateStr ? syncVersionOf(v) : null;
    const seen = this._diarySeen[dateStr];
    if (version && seen && seen.version === version && Date.now() - seen.at < DIARY_MAX_STALE_MS) return;
    this._diarySeen = version ? { [dateStr]: { version, at: Date.now() } } : {};
    this.loadDiaryData(this.state.bookingDate);
  }

  loadDiaryData = (date) => {
    if (!localStorage.getItem('userId')) return;

    const dateStr = moment(date).format('YYYY-MM-DD');

    this.setState({ isLoading: true });

    // Staging demo: render made-up bookings instead of calling the API. No
    // real data is read or written while this is on (Settings > Diary demo).
    // Bailey's welcome takes priority: it starts from the salon's own day.
    if (isDiaryDemoOn() && !isIntroActive()) {
      const demo = buildDemoDiary({ startDate: dateStr, endDate: dateStr });
      this.processDiaryResponse(demo, dateStr);
      return;
    }

    this.props.dispatch(hairdresserActions.getHairdresserScheduleReset());

    this.props.dispatch(hairdresserActions.getDiaryData({
      startDate: dateStr,
      endDate: dateStr,
    }))
      .then((response) => {
        // Onboarding: two temporary colleague columns, drawn in the browser
        // only, so a brand new salon sees what a working day looks like.
        const raw = response?.responseData || response?.data?.responseData || response;
        this._rawDiary = { data: raw, dateStr };
        const diaryData = this.shapeDiary(raw, dateStr);
        if (!diaryData?.staff) {
          this.setState({ isLoading: false });
          if (this.diaryRef.current) {
            this.diaryRef.current.dispatch({ type: ACTIONS.SET_INITIAL_LOAD_COMPLETE });
          }
          return;
        }

        this.processDiaryResponse(diaryData, dateStr);
        this.loadOtherSiteBlocks(diaryData, dateStr);
      })
      .catch(err => {
        console.error('Error loading diary data:', err);
        this.setState({ isLoading: false });
      });
  }

  // Multi-location (§14 M2): grey blocks for staff booked at another location.
  // Visibility only: stacking stays allowed, so a block never stops a booking.
  // Asked whenever the ACTIVE site is in a group, whatever the viewer's own
  // sites: a receptionist at one location still needs to see that the stylist
  // in front of them is at the other one this afternoon. The API returns
  // nothing for people who only work here. Single-site salons skip the call
  // (group is null in the shared /sites/mine cache).
  loadOtherSiteBlocks = (diaryData, date) => {
    try {
      const ids = (diaryData && diaryData.staff ? diaryData.staff : []).map(s => String(s._id || s.id)).filter(Boolean);
      const reqKey = `${date}|${ids.join(',')}`;
      this._otherSiteReq = reqKey;
      // Clear the previous day's blocks straight away; they are per date.
      if (this.diaryRef.current) this.diaryRef.current.dispatch({ type: ACTIONS.SET_OTHER_SITE_BLOCKS, payload: {} });
      if (!ids.length) return;
      const { sitesService } = require('../../_services/sites.service');
      const { loadSites } = require('../multiLocation/useSites');
      // An unreadable /sites/mine is not a reason to hide the blocks: ask anyway.
      loadSites(false).catch(() => null).then(sites => {
        if (sites && sites.group === null) return null;
        return sitesService.otherSiteBlocks(ids, date, date);
      }).then(res => {
        if (!res || this._otherSiteReq !== reqKey) return;   // a newer day/staff list has loaded since
        const byStaff = {};
        (res.blocks || []).forEach(b => {
          const k = String(b.userId);
          // One day is asked for, so every block is that day (slicing the
          // stored UTC timestamp can land on the previous day in BST).
          const slot = Array.isArray(b.bookingSlot) ? b.bookingSlot : [0, 0];
          (byStaff[k] = byStaff[k] || []).push({ date, startMinutes: slot[0], endMinutes: slot[1], salonName: b.salonName });
        });
        if (this.diaryRef.current) this.diaryRef.current.dispatch({ type: ACTIONS.SET_OTHER_SITE_BLOCKS, payload: byStaff });
      }).catch(() => {});
    } catch (e) { /* never break the diary */ }
  };

  processDiaryResponse = (diaryData, date) => {
    this._lastDiaryArgs = [diaryData, date];
    // Store salon timezone for NowIndicator
    if (diaryData.timezone) {
      localStorage.setItem('salonTimezone', diaryData.timezone);
    }
    const { staffMembers, eventsByStaff } = normalizeDiaryResponse(diaryData, date);

    // Filter staff by diary sharing visibility. The myVisibility
    // array from the diarySharing reducer contains the IDs of staff
    // whose diaries the current user is allowed to see. Manager
    // override (sees all) is handled in the stub/backend — if the
    // user is a manager, myVisibility includes every ID.
    const myVisibility = this.props.diaryVisibility || [];
    let visibleStaff = staffMembers;
    if (myVisibility.length > 0) {
      const allowed = new Set(myVisibility);
      // The diary demo's and Bailey's welcome tour's pretend team (ids
      // 'demo-...') are never on the server's list. Since that list started
      // arriving (staging, Oct 2026) they were all filtered out, leaving
      // "No staff to display" and a tour whose new colleagues never appeared.
      visibleStaff = staffMembers.filter((s) => allowed.has(s.id) || String(s.id).indexOf('demo-') === 0);
    }

    // Apply saved staff ordering (backend first, Redux fallback)
    let orderedStaff = visibleStaff;
    let savedOrderIds = null;

    // Backend-persisted order (returned with diary response)
    if (diaryData.staffOrder?.length > 0) {
      savedOrderIds = diaryData.staffOrder;
    }

    // Redux fallback (survives in-session navigation)
    if (!savedOrderIds) {
      const reduxOrder = this.props.dairyStaffOrderList;
      if (reduxOrder?.length > 0) {
        savedOrderIds = reduxOrder.map(s => s._id || s.id || s);
      }
    }

    if (savedOrderIds?.length > 0) {
      const orderMap = {};
      savedOrderIds.forEach((id, i) => { orderMap[id] = i; });
      // Sort the visible staff only: sorting staffMembers here brought back
      // every diary the sharing filter above had just hidden
      orderedStaff = [...visibleStaff].sort((a, b) => {
        const aIdx = orderMap[a.id] !== undefined ? orderMap[a.id] : 999;
        const bIdx = orderMap[b.id] !== undefined ? orderMap[b.id] : 999;
        return aIdx - bIdx;
      });
    }

    // Store for BookingDetailPanel edit mode
    this._staffList = orderedStaff;

    // What Bailey can see of the diary, for the desk pet (never gets in the
    // diary's way)
    try {
      const working = orderedStaff.filter(s => s.isWorking).length;
      const booked = orderedStaff.reduce((n, s) => n + (eventsByStaff[s.id] || []).filter(e => e.type === 'booking' && ![3, 6].includes(Number(e.status))).length, 0);
      publishFacts('diary', { path: '/diary', lines: [
        `Showing ${moment(date).format('dddd D MMMM')}`,
        `${working} of ${orderedStaff.length} stylists working, ${booked} ${booked === 1 ? 'booking' : 'bookings'}`,
      ] });
    } catch (e) { /* only for Bailey */ }

    const staffOrder = orderedStaff.map(s => s.id);
    const bounds = calculateTimeBounds(orderedStaff);
    // Kept for the way back from the week view.
    this._dayBounds = bounds;

    if (this.diaryRef.current) {
      const { dispatch } = this.diaryRef.current;
      dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: staffOrder });
      // The week view runs the whole day (0-24h). A day refresh underneath it
      // (booking saved, rooms or sharing arriving late) must not shrink it to
      // the day's hours, or later bookings sit below the grid, untappable.
      if (this.state.viewMode !== 'week') {
        dispatch({ type: ACTIONS.SET_TIME_BOUNDS, payload: bounds });
      }

      orderedStaff.forEach(s => {
        dispatch({
          type: ACTIONS.SET_STAFF_DATA,
          payload: { staffId: s.id, staff: s, events: eventsByStaff[s.id] || [] },
        });
      });

      // Resource lanes (docs/resources.md §5): rooms render with the SAME
      // column machinery, appended after staff. Their ids never persist into
      // the saved staff order (the filter excludes them from its drag lists).
      // The demo replaces the whole day, so the salon's real rooms stay out of
      // it and the demo brings its own (beauty has treatment rooms).
      const demoRes = isDiaryDemoOn() && !diaryData.isBaileyIntro ? demoResources() : null;
      // Bailey's welcome shows a new salon's day: no rooms yet.
      const units = diaryData.isBaileyIntro ? [] : demoRes ? demoRes.units : (this.state.resourceUnits || []);
      if (units.length) {
        enrichEventResourceColours(eventsByStaff, units);
        const groupsById = {};
        (demoRes ? demoRes.groups : (this.state.resourceGroups || [])).forEach(g => { groupsById[String(g._id)] = g; });
        const { laneStaff, eventsByLane } = buildResourceLanes(
          units, groupsById, staffMembers, eventsByStaff,
          // Demo staff are not in the real visibility list, and an empty list
          // means no restriction, so room bookings read normally in a demo.
          demoRes ? [] : myVisibility,
          diaryData.resourceEvents || [], date);
        laneStaff.forEach(lane => {
          dispatch({
            type: ACTIONS.SET_STAFF_DATA,
            payload: { staffId: lane.id, staff: lane, events: eventsByLane[lane.id] || [] },
          });
        });
        if (laneStaff.length) {
          dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: staffOrder.concat(laneStaff.map(l => l.id)) });
        }
      }

      dispatch({ type: ACTIONS.SET_INITIAL_LOAD_COMPLETE });
    }

    this.setState({ isLoading: false, isFirstTimeLoad: 0 });
  }

  handleDateChange = (newDate) => {
    const formatted = moment(newDate).format('YYYY-MM-DD');
    this.setState({ bookingDate: formatted });
    localStorage.setItem('cardBookingDate', formatted);

    if (this.diaryRef.current) {
      const { dispatch } = this.diaryRef.current;
      dispatch({ type: ACTIONS.SET_DATE, payload: formatted });
    }
    // The month view fetches its own range; the day loads on the way back.
    if (this.state.viewMode === 'month') { this._dayStale = true; return; }
    this.loadDiaryData(formatted);

    // In week view, also reload week data for the new date's week
    if (this.state.viewMode === 'week' && this._weekViewStaff) {
      this.loadWeekData(this._weekViewStaff, formatted);
    }
  }

  handleOpenCheckout = (bookingId) => {
    const siteSetting = require('../../config/env').default;
    if (siteSetting.useNewCheckout) {
      this.setState({ checkoutPanel: { bookingId }, selectedEvent: null });
    } else {
      this.props.history.push({ pathname: '/checkout/' + bookingId, state: { pageLoadingStatus: 1 } });
    }
  }

  handleEventClick = (event) => {
    // Bailey's welcome: a tap on a practice booking is part of the tour, so it
    // goes to Bailey rather than to a panel (there is nothing behind it).
    if (isIntroActive() && isDemoBookingId(event && event.bookingId)) {
      tellIntro('bookingTapped', { bookingId: event.bookingId, title: `${event.clientName} - ${event.title}` });
      return;
    }
    // Demo bookings exist only on this screen, so there is nothing to open.
    if (isDemoBookingId(event && event.bookingId) && !isDemoStaffId(event && event.staffId)) {
      toast.info('This is a practice booking. Real bookings open here once you start taking them.');
      return;
    }
    // The diary demo's team also has demo- ids, so it is checked first
    if (isDiaryDemoOn()) {
      toast.info('Demo booking. Turn the demo off in Settings to open real bookings.');
      return;
    }
    if (isDemoStaffId(event && event.staffId)) {
      toast.info('This column is an example. Tap its name to remove the examples.');
      return;
    }
    this.setState({ selectedEvent: event, contextMenuAction: null });
  }

  handleEventContextMenu = (event, x, y) => {
    if (isDemoBookingId(event && event.bookingId)) return;
    this.setState({ contextMenu: { event, x, y } });
  }

  handleContextMenuClose = () => {
    this.setState({ contextMenu: null });
  }

  handleContextMenuAction = (action, event) => {
    this.setState({ contextMenu: null });

    if (action === 'view') {
      this.setState({ selectedEvent: event, contextMenuAction: null });
    } else if (action === 'edit') {
      // Open side panel in edit mode
      this.setState({ selectedEvent: event, contextMenuAction: 'edit' });
    } else if (action === 'checkout') {
      if (event.bookingId) {
        this.handleOpenCheckout(event.bookingId);
      }
    } else if (action === 'cancel') {
      this.setState({ selectedEvent: event, contextMenuAction: 'cancel' });
    } else if (action === 'noshow') {
      this.setState({ selectedEvent: event, contextMenuAction: 'noshow' });
    } else if (action === 'park') {
      if (this.diaryRef.current) {
        const { dispatch } = this.diaryRef.current;
        dispatch({ type: ACTIONS.PARK_EVENT, payload: { event, originStaffId: event.staffId, originDate: this.state.bookingDate } });
      }
    } else if (action === 'editEvent') {
      this.setState({ selectedEvent: event, contextMenuAction: 'editEvent' });
    } else if (action === 'deleteEvent') {
      this.setState({ selectedEvent: event, contextMenuAction: 'deleteEvent' });
    }
    // The legacy 'viewFull' action used to navigate to /booking/:id,
    // /event-view/:id, and /leave-view/:id — all old screens that are
    // intentionally no longer reachable. The UI affordance has been removed
    // from BookingDetailPanel; the routes are gone from routes.js too.
  }

  handleClosePanel = () => {
    const wasDrop = this.state.contextMenuAction === 'drop' || this._hasPendingOptimisticUpdate;
    this._hasPendingOptimisticUpdate = false;
    this.setState({ selectedEvent: null, contextMenuAction: null });
    // If panel was opened from a drag-drop/resize and user closed without saving, reload to revert
    if (wasDrop) {
      this.loadDiaryData(this.state.bookingDate);
      if (this.state.viewMode === 'week' && this._weekViewStaff) {
        this.loadWeekData(this._weekViewStaff);
      }
      if (this.state.viewMode === 'month') refreshMonthView();
    }
  }

  handleSlotClick = (staff, startMinutes, actionType, dateOverride) => {
    const staffId = staff.id;
    const date = dateOverride || this.state.bookingDate;

    // If booking panel is minimised, any slot click re-points it at the new
    // slot (preserving the existing client/services/note in the draft).
    const mb = this.props.minimisedBooking;
    // A repeat's dates being checked: a tap must not move the booking (that
    // would move every visit). Placing a visit handles its own taps.
    if (getPlacement() || (mb?.active && mb?.isMinimised && isCheckingDates())) return;
    if (mb?.active && mb?.isMinimised) {
      this.props.dispatch(bookingActions.updateMinimisedBookingSlot({
        staffId, bookingDate: date, startMinutes,
      }));
      return;
    }

    if (actionType === 'appointment') {
      this.openNewBooking({ staffId, bookingDate: date, startMinutes });
      this.setState({
        selectedEvent: null,
      });
    } else if (actionType === 'event') {
      this.setState({
        newEventPanel: { mode: 'event', staffId, date, startMinutes },
        selectedEvent: null,
      });
    } else if (actionType === 'holiday') {
      this.setState({
        newEventPanel: { mode: 'leave', staffId, date, startMinutes },
        selectedEvent: null,
      });
    }
  }

  handleSlotDurationChange = (duration) => {
    if (!this.diaryRef.current) return;
    localStorage.setItem('diarySlotDuration', String(duration));
    this.setState({ slotDuration: duration });
    this.diaryRef.current.dispatch({ type: ACTIONS.SET_SLOT_DURATION, payload: duration });
  }

  handleZoomIn = () => {
    if (!this.diaryRef.current) return;
    const newZoom = Math.min(3.0, this._currentZoom + 0.25);
    this._currentZoom = newZoom;
    localStorage.setItem('diaryZoomLevel', String(newZoom));
    this.diaryRef.current.dispatch({ type: ACTIONS.SET_ZOOM, payload: newZoom });
  }

  handleZoomOut = () => {
    if (!this.diaryRef.current) return;
    const newZoom = Math.max(0.5, this._currentZoom - 0.25);
    this._currentZoom = newZoom;
    localStorage.setItem('diaryZoomLevel', String(newZoom));
    this.diaryRef.current.dispatch({ type: ACTIONS.SET_ZOOM, payload: newZoom });
  }

  handleFilterToggle = () => {
    this.setState(prev => ({ filterOpen: !prev.filterOpen }));
  }

  handleEventDrop = (event, fromStaffId, toStaffId, dropMinutes) => {
    // For chained segments (multi-service with processing), calculate total booking duration
    let duration = event.endMinutes - event.startMinutes;
    let newStartMinutes = dropMinutes;
    if (event.isServiceSegment && event.chainId && fromStaffId !== toStaffId && this.diaryRef.current) {
      const allEvents = this.diaryRef.current.state.eventsByStaff[fromStaffId] || [];
      const chainEvents = allEvents.filter(e => e.chainId === event.chainId);
      if (chainEvents.length > 1) {
        const chainStart = Math.min(...chainEvents.map(e => e.startMinutes));
        const chainEnd = Math.max(...chainEvents.map(e => e.endMinutes));
        const timeDelta = dropMinutes - event.startMinutes;
        duration = chainEnd - chainStart;
        newStartMinutes = chainStart + timeDelta;
      }
    }
    const newEndMinutes = newStartMinutes + duration;

    // Dropping from shelf - event already removed from shelf by UNPARK_EVENT in DragOverlay
    // Just add to target column and call the API
    if (fromStaffId === '__shelf__') {
      const realToStaffId = toStaffId && toStaffId.includes('_20') ? toStaffId.split('_')[0] : toStaffId;
      const isWeekDrop = toStaffId && toStaffId.includes('_20');
      const targetDate = isWeekDrop ? toStaffId.split('_').slice(1).join('_') : this.state.bookingDate;

      // Add all chain events to target column (multi-service bookings with processing gaps)
      if (this.diaryRef.current) {
        const timeDelta = newStartMinutes - event.startMinutes;
        const chainEvents = event._chainEvents;
        const eventsToAdd = chainEvents
          ? chainEvents.map(e => ({ ...e, staffId: realToStaffId, startMinutes: e.startMinutes + timeDelta, endMinutes: e.endMinutes + timeDelta }))
          : [{ ...event, staffId: realToStaffId, startMinutes: newStartMinutes, endMinutes: newEndMinutes }];

        if (isWeekDrop) {
          this.diaryRef.current.dispatch({
            type: ACTIONS.SET_WEEK_DATA,
            payload: { ...this.diaryRef.current.state.weekData, [targetDate]: [...(this.diaryRef.current.state.weekData[targetDate] || []), ...eventsToAdd] },
          });
        } else {
          const existing = this.diaryRef.current.state.eventsByStaff[realToStaffId] || [];
          this.diaryRef.current.dispatch({
            type: ACTIONS.SET_EVENTS,
            payload: { staffId: realToStaffId, events: [...existing, ...eventsToAdd] },
          });
        }
      }

      if (event.type === 'booking') {
        this.handleBookingDrop(event, realToStaffId, newStartMinutes, newEndMinutes, isWeekDrop, targetDate);
      } else if (event.type === 'event') {
        this.handleEventDropApi(event, newStartMinutes, newEndMinutes, isWeekDrop, targetDate);
      }
      return;
    }

    // Week view uses composite IDs (staffId_YYYY-MM-DD)
    const isWeekDrop = toStaffId && toStaffId.includes('_20');
    const realStaffId = isWeekDrop ? toStaffId.split('_')[0] : toStaffId;

    if (isWeekDrop) {
      // Extract target date from composite ID (staffId_YYYY-MM-DD)
      const targetDate = toStaffId.split('_').slice(1).join('_');
      const fromDateKey = fromStaffId && fromStaffId.includes('_20') ? fromStaffId.split('_').slice(1).join('_') : targetDate;

      // Optimistic update for week view
      if (this.diaryRef.current) {
        this.diaryRef.current.dispatch({
          type: ACTIONS.WEEK_MOVE_EVENT,
          payload: { eventId: event.id, fromDateKey, toDateKey: targetDate, newStartMinutes },
        });
      }

      // Same-day chain segment adjustment for week view: pass segment
      // positions so the edit panel recalculates processing times against
      // the chain (same as day view does — line ~471). Without this, a
      // chain segment move within the same day on weekly view falls
      // through to handleDropEdit's no-segments branch, which then
      // cascade-shrinks service durations.
      if (event.isServiceSegment && event.chainId && fromDateKey === targetDate && this.diaryRef.current) {
        const allEvents = this.diaryRef.current.state.weekData[targetDate] || [];
        const chainEvents = allEvents.filter(e => e.chainId === event.chainId).sort((a, b) => a.startMinutes - b.startMinutes);
        if (chainEvents.length > 1 && event.type === 'booking') {
          const chainStart = Math.min(...chainEvents.map(e => e.startMinutes));
          const chainEnd = Math.max(...chainEvents.map(e => e.endMinutes));
          this.handleBookingDrop(event, realStaffId, chainStart, chainEnd, true, targetDate, chainEvents);
          return;
        }
      }

      if (event.type === 'booking') {
        this.handleBookingDrop(event, realStaffId, newStartMinutes, newEndMinutes, true, targetDate);
      } else if (event.type === 'event') {
        this.handleEventDropApi(event, newStartMinutes, newEndMinutes, true, targetDate);
      }
      return;
    }

    // Day view: optimistic local update
    if (this.diaryRef.current) {
      this.diaryRef.current.dispatch({
        type: ACTIONS.MOVE_EVENT,
        payload: { eventId: event.id, fromStaffId, toStaffId, newStartMinutes },
      });
    }

    // Same-column chain segment adjustment: pass updated segment positions
    if (event.isServiceSegment && event.chainId && fromStaffId === toStaffId && this.diaryRef.current) {
      const allEvents = this.diaryRef.current.state.eventsByStaff[toStaffId] || [];
      const chainEvents = allEvents.filter(e => e.chainId === event.chainId).sort((a, b) => a.startMinutes - b.startMinutes);
      if (chainEvents.length > 1 && event.type === 'booking') {
        const chainStart = Math.min(...chainEvents.map(e => e.startMinutes));
        const chainEnd = Math.max(...chainEvents.map(e => e.endMinutes));
        // Pass segment positions so edit panel can recalculate processing times
        this.handleBookingDrop(event, toStaffId, chainStart, chainEnd, false, null, chainEvents);
        return;
      }
    }

    if (event.type === 'booking') {
      this.handleBookingDrop(event, toStaffId, newStartMinutes, newEndMinutes);
    } else if (event.type === 'event') {
      this.handleEventDropApi(event, newStartMinutes, newEndMinutes);
    }
  }

  reloadWeekIfNeeded = (isWeekView) => {
    if (!isWeekView) return;
    if (this._weekViewStaff) {
      this.loadWeekData(this._weekViewStaff);
    }
  }

  handleBookingDrop = (event, toStaffId, newStart, newEnd, isWeekView, targetDate, segmentPositions, edge) => {
    this._hasPendingOptimisticUpdate = true;
    // Open the booking detail panel in edit mode with the drop overrides
    // User must confirm the change — services are validated against the target stylist
    const dropOverrides = {
      staffId: toStaffId,
      startMinutes: newStart,
      endMinutes: newEnd,
      date: targetDate || this.state.bookingDate,
      isWeekView: !!isWeekView,
      segmentPositions: segmentPositions || null,
      edge: edge || null,
    };

    this.setState({
      selectedEvent: { ...event, _dropOverrides: dropOverrides },
      contextMenuAction: 'drop',
    });
  }

  handleEventResize = (event, newStartMinutes, newEndMinutes) => {
    const staffId = event.staffId;
    const isWeek = staffId && staffId.includes('_20');
    const realStaffId = isWeek ? staffId.split('_')[0] : staffId;
    const eventDate = isWeek ? staffId.split('_').slice(1).join('_') : null;

    // Optimistic local update
    if (this.diaryRef.current) {
      if (isWeek) {
        this.diaryRef.current.dispatch({
          type: ACTIONS.WEEK_RESIZE_EVENT,
          payload: { eventId: event.id, dateKey: eventDate, newStartMinutes, newEndMinutes },
        });
      } else {
        this.diaryRef.current.dispatch({
          type: ACTIONS.RESIZE_EVENT,
          payload: { eventId: event.id, staffId, newStartMinutes, newEndMinutes },
        });
      }
    }

    // For chain segment resize: use full chain span with segment positions.
    // Pass the resize edge so handleDropEdit knows this is a resize (not a
    // move) and propagates the new segment durations to serviceTime.
    if (event.isServiceSegment && event.chainId && this.diaryRef.current) {
      const lookupKey = isWeek ? eventDate : staffId;
      const allEvents = (isWeek ? this.diaryRef.current.state.weekData[lookupKey] : this.diaryRef.current.state.eventsByStaff[lookupKey]) || [];
      const chainEvents = allEvents.filter(e => e.chainId === event.chainId).sort((a, b) => a.startMinutes - b.startMinutes);
      if (chainEvents.length > 1 && event.type === 'booking') {
        const chainStart = Math.min(...chainEvents.map(e => e.startMinutes));
        const chainEnd = Math.max(...chainEvents.map(e => e.endMinutes));
        const resizeEdge = (newStartMinutes !== event.startMinutes && newEndMinutes === event.endMinutes) ? 'top' : 'bottom';
        this.handleBookingDrop(event, realStaffId, chainStart, chainEnd, isWeek, eventDate, chainEvents, resizeEdge);
        return;
      }
    }

    if (event.type === 'booking') {
      // Store resize data in sessionStorage so the edit panel can read it
      // (passing through _dropOverrides on the event prop is unreliable due to React re-renders)
      try {
        sessionStorage.setItem('beu-resize', JSON.stringify({
          startMinutes: newStartMinutes,
          endMinutes: newEndMinutes,
          edge: (newStartMinutes !== event.startMinutes && newEndMinutes === event.endMinutes) ? 'top' : 'bottom',
        }));
      } catch (e) { /* ignore */ }
      this.handleBookingDrop(event, realStaffId, newStartMinutes, newEndMinutes, isWeek, eventDate);
    } else if (event.type === 'event') {
      this.handleEventDropApi(event, newStartMinutes, newEndMinutes, isWeek, eventDate);
    }
  }

  handleEventDropApi = (event, newStart, newEnd, isWeekView, targetDate) => {
    const raw = event.raw;
    const eventInfo = raw?.list?.[0]?.info || raw?.info?.list?.[0]?.info || raw?.info;

    if (!eventInfo) {
      toast.error('Could not move event');
      if (isWeekView) {
        this.reloadWeekIfNeeded(true);
      } else {
        this.loadDiaryData(this.state.bookingDate);
      }
      return;
    }

    const params = {
      eventId: eventInfo._id,
      title: eventInfo.title,
      date: targetDate || this.state.bookingDate,
      startTime: newStart,
      endTime: newEnd,
      isBookingAllowed: !!eventInfo.isBookingAllowed,
      hairdresserId: eventInfo.userId,
      repeatType: eventInfo.repeatType || 'doesNotRepeat',
      diaryColour: eventInfo.diaryColour || null,
      emoji: eventInfo.emoji || null,
    };

    this.props.dispatch(eventActions.editEvent(params))
      .then(response => {
        if (response?.data?.errorCode) {
          toast.error(response.data.messages || 'Could not move event');
        }
        if (isWeekView) {
          this.reloadWeekIfNeeded(true);
        } else if (response?.data?.errorCode) {
          this.loadDiaryData(this.state.bookingDate);
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
        if (isWeekView) {
          this.reloadWeekIfNeeded(true);
        } else {
          this.loadDiaryData(this.state.bookingDate);
        }
      });
  }

  handleBackToDay = () => {
    this.setState({ viewMode: 'day' });
    this._weekViewStaff = null;
    this._monthViewStaff = null;
    // The month changed while it was open: load the day it landed on.
    if (this._dayStale) { this._dayStale = false; this.loadDiaryData(this.state.bookingDate); }
    if (this.diaryRef.current) {
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'day' });
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: null });
      if (this._dayBounds) this.diaryRef.current.dispatch({ type: ACTIONS.SET_TIME_BOUNDS, payload: this._dayBounds });
    }
  }

  handleDayTap = (dateKey, staffId) => {
    // Switch to day view for the tapped date
    // CalendarCard reads its date once, on mount, so remount it on the new day.
    this.setState(prev => ({ viewMode: 'day', bookingDate: dateKey, calendarKey: (prev.calendarKey || 0) + 1 }));
    this._weekViewStaff = null;
    this._monthViewStaff = null;
    this._dayStale = false;
    // From the month view: bring that stylist's column into view.
    if (staffId) this.scrollToStaffColumn(staffId);
    localStorage.setItem('cardBookingDate', dateKey);
    if (this.diaryRef.current) {
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_DATE, payload: dateKey });
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'day' });
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: null });
    }
    this.loadDiaryData(dateKey);
  }

  // Scroll the day view sideways so this stylist's column is in view. The
  // day re-renders after a load, so wait (briefly) for the column to exist.
  scrollToStaffColumn = (staffId) => {
    const started = Date.now();
    const attempt = () => {
      if (this._unmounted) return;
      const sel = `.dv2-staff-column[data-staff-id="${String(staffId).replace(/"/g, '')}"]`;
      const col = document.querySelector(sel);
      const vp = col && col.closest('.dv2-viewport');
      if (col && vp && !this.state.isLoading) {
        const vpRect = vp.getBoundingClientRect();
        const colRect = col.getBoundingClientRect();
        const axis = vp.querySelector('.dv2-time-axis');
        const axisW = axis ? axis.getBoundingClientRect().width : 0;
        const visibleW = vpRect.width - axisW;
        const offset = colRect.left - (vpRect.left + axisW) - Math.max(0, (visibleW - colRect.width) / 2);
        if (Math.abs(offset) > 2) vp.scrollTo({ left: vp.scrollLeft + offset, behavior: 'smooth' });
        return;
      }
      if (Date.now() - started < 4000) setTimeout(attempt, 120);
    };
    setTimeout(attempt, 60);
  }

  handleStaffHeaderAction = (staff, action) => {
    // The onboarding example colleagues: take them off the diary for good
    if (action === 'removeExamples') {
      setDemoTeam(false);
      toast.success('Example columns removed');
      return;
    }
    // Social: New post at this stylist's free times for the day on screen
    if (action === 'postFreeTimes') {
      this.props.history.push(...freeTimesFor(staff, this.state.bookingDate));
      return;
    }
    if (action === 'editShift') {
      this.openEditShift(staff);
    } else if (action === 'viewWeek') {
      this._weekViewStaff = staff;
      this.setState({ viewMode: 'week' });
      if (this.diaryRef.current) {
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: staff.id });
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'week' });
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_TIME_BOUNDS, payload: { start: 0, end: 1440 } });
      }
      this.loadWeekData(staff);
    } else if (action === 'viewMonth') {
      this._weekViewStaff = null;
      this._monthViewStaff = staff;
      this.setState({ viewMode: 'month' });
      if (this.diaryRef.current) {
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: staff.id });
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'month' });
      }
    }
  }

  // Month view data: the same diary call the week view makes, for the whole
  // calendar grid (padded to whole weeks), one stylist. Resolves to the week
  // view's shape ({ weekData, weekStaffInfo } keyed by date). The server
  // applies the viewer's diary visibility, as it does for the day and week.
  loadMonthData = (staff, startDate, endDate) => {
    if (isDiaryDemoOn()) {
      const demo = buildDemoDiary({ startDate, endDate, staffIds: staff.id });
      return Promise.resolve(normalizeDiaryWeekResponse(demo, staff.id));
    }
    // The API caps a request at 31 days and a grid can be 42, so ask in
    // four-week chunks and merge. A refused chunk fails the month (rather than
    // drawing every day as "Off").
    const chunks = splitDateRange(startDate, endDate, 28);
    return Promise.all(chunks.map(c =>
      this.props.dispatch(hairdresserActions.getDiaryData({ startDate: c.startDate, endDate: c.endDate, staffIds: staff.id }))
        .then((response) => {
          const diaryData = response?.responseData || response?.data?.responseData || response;
          if (!diaryData?.dates) throw new Error((response && response.error && response.error.message) || 'Diary data unavailable');
          return normalizeDiaryWeekResponse(diaryData, staff.id);
        })))
      .then(parts => parts.reduce((acc, p) => ({
        weekData: { ...acc.weekData, ...p.weekData },
        weekStaffInfo: { ...acc.weekStaffInfo, ...p.weekStaffInfo },
      }), { weekData: {}, weekStaffInfo: {} }));
  }

  loadWeekData = (staff, dateOverride) => {
    const base = moment(dateOverride || this.state.bookingDate);
    const monday = base.clone().startOf('isoWeek');
    const sunday = monday.clone().add(6, 'days');

    // Show empty state immediately so UI renders skeleton
    const emptyWeekData = {};
    const emptyWeekStaffInfo = {};
    for (let i = 0; i < 7; i++) {
      const dayDate = monday.clone().add(i, 'days').format('YYYY-MM-DD');
      emptyWeekData[dayDate] = [];
      emptyWeekStaffInfo[dayDate] = { isWorking: false, startMinutes: null, endMinutes: null };
    }

    if (this.diaryRef.current) {
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_DATA, payload: emptyWeekData });
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_STAFF_INFO, payload: emptyWeekStaffInfo });
    }

    if (isDiaryDemoOn()) {
      const demo = buildDemoDiary({ startDate: monday.format('YYYY-MM-DD'), endDate: sunday.format('YYYY-MM-DD'), staffIds: staff.id });
      const { weekData, weekStaffInfo } = normalizeDiaryWeekResponse(demo, staff.id);
      if (this.diaryRef.current) {
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_DATA, payload: weekData });
        this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_STAFF_INFO, payload: weekStaffInfo });
      }
      return;
    }

    // Single API call for the whole week for this staff member
    this.props.dispatch(hairdresserActions.getDiaryData({
      startDate: monday.format('YYYY-MM-DD'),
      endDate: sunday.format('YYYY-MM-DD'),
      staffIds: staff.id,
    }))
      .then((response) => {
        const diaryData = response?.responseData || response?.data?.responseData || response;
        if (!diaryData?.dates) return;

        const { weekData, weekStaffInfo } = normalizeDiaryWeekResponse(diaryData, staff.id);

        if (this.diaryRef.current) {
          this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_DATA, payload: weekData });
          this.diaryRef.current.dispatch({ type: ACTIONS.SET_WEEK_STAFF_INFO, payload: weekStaffInfo });
        }
      })
      .catch((err) => {
        console.error('Error loading week data:', err);
      });
  }

  openEditShift = (staff) => {
    // Read shift times from normalized staff data
    let todayStartWorking = '';
    let todayEndWorking = '';
    let isTodayWorking = false;

    if (staff.isWorking && staff.startMinutes != null && staff.endMinutes != null) {
      const startHr = Math.floor(staff.startMinutes / 60);
      const startMin = staff.startMinutes % 60;
      todayStartWorking = new Date(new Date().setHours(startHr, startMin, 0, 0));

      const endHr = Math.floor(staff.endMinutes / 60);
      const endMin = staff.endMinutes % 60;
      todayEndWorking = new Date(new Date().setHours(endHr, endMin, 0, 0));
      isTodayWorking = true;
    }

    this.setState({
      shiftStaff: staff,
      todayStartWorking,
      todayEndWorking,
      originalStartWorking: todayStartWorking,
      originalEndWorking: todayEndWorking,
      isTodayWorking,
      openShiftDay: true,
    });
  }

  handleShiftClose = () => {
    this.setState({ openShiftDay: false, shiftStaff: null });
  }

  handleShiftTimeChange = (field, value) => {
    this.setState({ [field]: value });
  }

  getMinutesFromDate = (date) => {
    if (!date || date === '') return 0;
    const d = new Date(date);
    return d.getHours() * 60 + d.getMinutes();
  }

  saveShift = () => {
    const startMin = this.getMinutesFromDate(this.state.todayStartWorking);
    const endMin = this.getMinutesFromDate(this.state.todayEndWorking);

    if (!startMin) {
      toast.error('Please select a start time');
      return;
    }
    if (!endMin) {
      toast.error('Please select an end time');
      return;
    }
    if (startMin >= endMin) {
      toast.error('End time must be after start time');
      return;
    }

    const params = {
      isRemoved: 2,
      workingDate: moment(this.state.bookingDate).format('YYYY-MM-DD'),
      startTime: startMin,
      endTime: endMin,
      hairdresserId: this.state.shiftStaff.id,
    };

    this.props.dispatch(hairdresserActions.saveWorkingRota(params))
      .then((res) => {
        // v2 refusals (validation, no permission) arrive as a resolved
        // { errorCode, messages } — keep the sheet open and say why.
        if (res && res.data && res.data.errorCode) {
          toast.error(res.data.messages || 'Something went wrong');
          return;
        }
        this.setState({ openShiftDay: false, shiftStaff: null });
        this.loadDiaryData(this.state.bookingDate);
      })
      .catch(() => {
        toast.error('Something went wrong');
      });
  }

  deleteShift = () => {
    const startMin = this.getMinutesFromDate(this.state.todayStartWorking);
    const endMin = this.getMinutesFromDate(this.state.todayEndWorking);

    const params = {
      isRemoved: 1,
      workingDate: moment(this.state.bookingDate).format('YYYY-MM-DD'),
      startTime: startMin,
      endTime: endMin,
      hairdresserId: this.state.shiftStaff.id,
    };

    this.props.dispatch(hairdresserActions.saveWorkingRota(params))
      .then((res) => {
        // v2 refusals (validation, no permission) arrive as a resolved
        // { errorCode, messages } — keep the sheet open and say why.
        if (res && res.data && res.data.errorCode) {
          toast.error(res.data.messages || 'Something went wrong');
          return;
        }
        this.setState({ openShiftDay: false, shiftStaff: null });
        this.loadDiaryData(this.state.bookingDate);
      })
      .catch(() => {
        toast.error('Something went wrong');
      });
  }

  handleStaffReorder = (reorderedStaffList) => {
    // Two lists, deliberately different: the BACKEND save carries humans only
    // (a res_* id must never pollute the persisted staff order), while the
    // context order re-appends the room lanes so they don't vanish on reorder.
    const humans = reorderedStaffList.filter(s => !(s && s.isResource) && !String(s._id || s.id || '').startsWith('res_'));
    const orderIds = humans.map(s => s._id || s.id).filter(Boolean);
    const contextOrderIds = orderIds.concat(
      (this.state.resourceUnits || [])
        .filter(u => !u.isRemoved && u.isActive !== false)
        .map(u => 'res_' + String(u._id)));

    // Persist to Redux so it survives navigation
    this.props.dispatch(hairdresserActions.dairyFilterStaffOrderList(reorderedStaffList));

    // Update DiaryContext staff order to reflect immediately (lanes included)
    if (this.diaryRef.current) {
      this.diaryRef.current.dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: contextOrderIds });
    }

    // Persist to backend so it syncs across devices. Surface the error if
    // it fails — previously this was a silent catch, which is how we shipped
    // a non-persisting reorder without noticing.
    this.props.dispatch(hairdresserActions.saveDiaryStaffOrder(orderIds))
      .then((result) => {
        console.log('[staff reorder] saved', result);
      })
      .catch((err) => {
        console.error('[staff reorder] save failed', err);
        toast.error('Could not save staff order. The new order will reset on refresh.');
      });
  }

  render() {
    const { bookingDate, isFirstTimeLoad, filterOpen, selectedEvent, openShiftDay, shiftStaff, contextMenu, contextMenuAction } = this.state;

    return (
      <>
        <div className={`dairy-container-header dv2-page`}>
          <TopHeader title="" {...this.props} />
          <CalendarCard
            key={this.state.calendarKey || 0}
            bookingDate={bookingDate}
            isFirstTimeLoad={isFirstTimeLoad}
            setCalenderDate={this.handleDateChange}
            onFilterClick={this.handleFilterToggle}
            filterActive={filterOpen}
            onZoomIn={this.handleZoomIn}
            onZoomOut={this.handleZoomOut}
            onSlotDurationChange={this.handleSlotDurationChange}
            slotDuration={this.state.slotDuration}
            viewMode={this.state.viewMode}
          />
        </div>
        {/* The walk round the diary comes first, then the guided first tasks. */}
        {/* Bailey's welcome, the very first thing a new owner sees. */}
        {/* None of it on a device locked in reception mode. */}
        {ONBOARDING_ENABLED && !receptionSetupOff() && <BaileyIntro />}
        {ONBOARDING_ENABLED && !receptionSetupOff() && <DiaryTour />}
        {/* Guided first tasks + ?setup=1 entry (replaces the five-row checklist) */}
        {ONBOARDING_ENABLED && !receptionSetupOff() && <FirstTasks />}
        <DiaryProvider initialDate={bookingDate} onEventClick={this.handleEventClick} onEventContextMenu={this.handleEventContextMenu} onSlotClick={this.handleSlotClick} skipSlotPopup={!!(this.props.minimisedBooking?.active && this.props.minimisedBooking?.isMinimised) || isIntroActive()} onEventDrop={this.handleEventDrop} onEventResize={this.handleEventResize} onStaffHeaderAction={this.handleStaffHeaderAction} onBackToDay={this.handleBackToDay} onDayTap={this.handleDayTap} onLoadMonth={this.loadMonthData} onMonthChange={this.handleDateChange}>
          <DragProvider>
            <DiaryContextBridge ref={this.diaryRef} />
            <StaffFilter open={filterOpen} onToggle={this.handleFilterToggle} onReorder={this.handleStaffReorder} />
            <DiaryShelf ref={this.shelfRef} onEventClick={this.handleEventClick} />
            <div className={`dv2-container${this.state.viewMode === 'month' ? ' dv2-container--month' : ''}`}>
              <DiaryViewport />
            </div>
            <DragOverlay shelfRef={this.shelfRef} />
          </DragProvider>
        </DiaryProvider>
        <DiaryFAB onAction={(type) => {
          const loggedInUserId = this.props.user?.userInfo?.hairdresser?.userId || this.props.user?.userInfo?.hairdresser?._id || localStorage.getItem('userId');
          const myStaff = this._staffList?.find(s => s.id === loggedInUserId);
          const firstStaff = myStaff || this._staffList?.find(s => s.isWorking) || this._staffList?.[0];
          if (type === 'booking') {
            this.openNewBooking({ staffId: firstStaff?.id || '' });
            this.setState({ selectedEvent: null });
          } else {
            this.setState({ newEventPanel: { mode: type, staffId: firstStaff?.id || '' }, selectedEvent: null });
          }
        }} />
        {selectedEvent && (
          <BookingDetailPanel
            event={selectedEvent}
            bookingDate={bookingDate}
            onClose={this.handleClosePanel}
            onOpenCheckout={(bookingId) => this.handleOpenCheckout(bookingId)}
            dispatch={this.props.dispatch}
            history={this.props.history}
            currencySymbol={this.props.user?.userInfo?.hairdresser?.currencySymbol || '£'}
            initialAction={contextMenuAction}
            staffList={this._staffList || []}
            onRefresh={() => {
              this._hasPendingOptimisticUpdate = false;
              // Time off being added over it: its list checks again
              if (this.state.newEventPanel) this.setState(st => ({ bookingsVersion: (st.bookingsVersion || 0) + 1 }));
              this.loadDiaryData(this.state.bookingDate);
              if (this.state.viewMode === 'week' && this._weekViewStaff) {
                this.loadWeekData(this._weekViewStaff);
              }
              if (this.state.viewMode === 'month') refreshMonthView();
            }}
            onRebook={({ staffId, services, client }) => {
              this.openNewBooking({ staffId, rebookServices: services, rebookClient: client });
              this.setState({ selectedEvent: null });
            }}
          />
        )}
        {this.state.checkoutPanel && (
          <CheckoutPanel
            bookingId={this.state.checkoutPanel.bookingId}
            onClose={() => this.setState({ checkoutPanel: null })}
            onComplete={() => {
              this.setState({ checkoutPanel: null });
              this.loadDiaryData(this.state.bookingDate);
              if (this.state.viewMode === 'week' && this._weekViewStaff) {
                this.loadWeekData(this._weekViewStaff);
              }
              if (this.state.viewMode === 'month') refreshMonthView();
            }}
            onRebook={({ staffId, services, client }) => {
              this.openNewBooking({ staffId, rebookServices: services, rebookClient: client });
              this.setState({ checkoutPanel: null });
            }}
            dispatch={this.props.dispatch}
            history={this.props.history}
            currencySymbol={this.props.user?.userInfo?.hairdresser?.currencySymbol || '£'}
          />
        )}
        {/* NewBookingPanel is rendered globally by NewBookingHost (mounted
            in routes.js) so the minimised pill survives route changes. */}
        {this.state.newEventPanel && (
          <NewEventPanel
            key={this.state.newEventPanel.key || 'slot'}
            {...(this.state.newEventPanel.prefill || {})}
            mode={this.state.newEventPanel.mode}
            staffId={this.state.newEventPanel.staffId}
            bookingDate={this.state.newEventPanel.date}
            startMinutes={this.state.newEventPanel.startMinutes}
            staffList={this._staffList || []}
            resourceUnits={this.state.resourceUnits || []}
            bookingsVersion={this.state.bookingsVersion || 0}
            onOpenBooking={this.openBookingOverTimeOff}
            onClose={() => this.setState({ newEventPanel: null })}
            onCreated={(info) => {
              this.setState({ newEventPanel: null });
              // Land it like a booking, and let Bailey know it was saved.
              if (info && info.staffId) {
                highlightItem({ kind: info.kind, staffId: info.staffId, startMinutes: info.startMinutes });
                window.dispatchEvent(new CustomEvent(ITEM_CREATED_EVENT, { detail: info }));
              }
              this.loadDiaryData(this.state.bookingDate);
              if (this.state.viewMode === 'week' && this._weekViewStaff) {
                this.loadWeekData(this._weekViewStaff);
              }
              if (this.state.viewMode === 'month') refreshMonthView();
            }}
            dispatch={this.props.dispatch}
          />
        )}
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            event={contextMenu.event}
            onAction={this.handleContextMenuAction}
            onClose={this.handleContextMenuClose}
            parkedCount={this.diaryRef.current?.state?.parkedEvents?.length || 0}
          />
        )}
        {openShiftDay && shiftStaff && (
          <SlidePanel
            title="Edit Shift"
            onClose={this.handleShiftClose}
            footer={
              <div className="shift-sheet-actions">
                {this.state.isTodayWorking && (
                  <button className="shift-sheet-btn shift-sheet-btn-delete" onClick={this.deleteShift}>Delete</button>
                )}
                <button
                  className="shift-sheet-btn shift-sheet-btn-save"
                  onClick={this.saveShift}
                  disabled={
                    String(this.state.todayStartWorking) === String(this.state.originalStartWorking) &&
                    String(this.state.todayEndWorking) === String(this.state.originalEndWorking)
                  }
                >Save</button>
              </div>
            }
          >
            <h5 className="shift-sheet-name">{shiftStaff.name}'s shift for {moment(bookingDate).format('dddd, DD MMMM YY')}</h5>
            <p className="shift-sheet-desc">This adjustment will only apply for this specific day. To make a permanent schedule change, go to <Link to="/staff/rota" style={{color: '#007EA0', fontWeight: '600'}}>rota</Link>.</p>
            <div className="shift-sheet-times">
              <div className="shift-sheet-time-field">
                <label>Start Time</label>
                <ShiftTimePicker
                  label="Start Time"
                  selected={this.state.todayStartWorking}
                  placeholder="9:00"
                  onChange={(val) => this.handleShiftTimeChange('todayStartWorking', val)}
                />
              </div>
              <div className="shift-sheet-time-field">
                <label>Finish Time</label>
                <ShiftTimePicker
                  label="Finish Time"
                  selected={this.state.todayEndWorking}
                  placeholder="17:00"
                  onChange={(val) => this.handleShiftTimeChange('todayEndWorking', val)}
                />
              </div>
            </div>
          </SlidePanel>
        )}
      </>
    );
  }
}

/**
 * Bridge component to expose DiaryContext dispatch to the class component.
 */
const DiaryContextBridge = React.forwardRef((props, ref) => {
  const { useDiary } = require('./DiaryContext');
  const { state, dispatch } = useDiary();

  React.useImperativeHandle(ref, () => ({ state, dispatch }), [state, dispatch]);

  return null;
});

function mapStateToProps(state) {
  return {
    ...state,
    dairyStaffOrderList: state.dairyStaffOrderList,
    diaryVisibility: state.diarySharing?.myVisibility || [],
  };
}

export default withRouter(connect(mapStateToProps)(DiaryV2));
